import React, { useEffect, useMemo, useState } from 'react';
import { Box, Stepper, Step, StepLabel, Button, Paper, Snackbar, Alert, Typography, CircularProgress, IconButton } from '@mui/material';
import { useWizard } from '../context/WizardContext';
import { submitToTaxAlpha } from '../services/api';
import { analyticsService } from '../services/analyticsService';
import { progressStorageService } from '../services/progressStorageService';
import WelcomeStep from './steps/WelcomeStep';
import PersonalInfoStep from './steps/PersonalInfoStep';
import EmploymentStep from './steps/EmploymentStep';
import SocialSecurityStep from './steps/SocialSecurityStep';
import SavingsStep from './steps/SavingsStep';
import GoalsStep from './steps/GoalsStep';
import ReviewStep from './steps/ReviewStep';
import ThankYouStep from './steps/ThankYouStep';
import BasicInfoStep from './steps/BasicInfoStep';
import BasicFinancialsStep from './steps/BasicFinancialsStep';
import BasicOptionalStep from './steps/BasicOptionalStep';
import BasicReviewStep from './steps/BasicReviewStep';
import HealthSupportStep from './steps/HealthSupportStep';
import logo from '../assets/PlanScout_orange_Logo.jpg';
import { useParams } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import { useAdvisor } from '../hooks/useAdvisor';
import { DEFAULT_CUSTOMER_ID } from '../types/advisor';
import { useProgressTracking } from '../hooks/useProgressTracking';

// Function to clear wizard session
const clearWizardStorage = () => {
  // Only clear sessionStorage as data is server-side
  sessionStorage.removeItem('wizardSessionId');
};

const isBasicIntake = (v?: string) => v === 'basic' || v === 'basic-healthspan';

// Ordered step keys for the basic (Limited) intake. The HealthSpan variant
// inserts a health step before review. Routing / labels / validation all index
// off this array, so the extra step never desyncs downstream indices.
const BASIC_STEP_LABELS: Record<string, { label: string; mobile: string }> = {
  start: { label: 'Start', mobile: 'Start' },
  about: { label: 'About You', mobile: 'You' },
  income: { label: 'Income & Savings', mobile: 'Finances' },
  optional: { label: 'Optional Details', mobile: 'Details' },
  health: { label: 'Health & Support', mobile: 'Health' },
  healthPartner: { label: 'Partner Health', mobile: 'Partner' },
  review: { label: 'Contact & Review', mobile: 'Review' },
};

const getBasicStepKeys = (intakeVersion?: string, hasPartner?: boolean): string[] => {
  const keys = ['start', 'about', 'income', 'optional'];
  if (intakeVersion === 'basic-healthspan') {
    keys.push('health');
    if (hasPartner) keys.push('healthPartner');
  }
  keys.push('review');
  return keys;
};

const getStepsForIntake = (intakeVersion?: string, hasPartner?: boolean) => {
  if (isBasicIntake(intakeVersion)) {
    const keys = getBasicStepKeys(intakeVersion, hasPartner);
    return {
      steps: keys.map((k) => BASIC_STEP_LABELS[k].label),
      mobileSteps: keys.map((k) => BASIC_STEP_LABELS[k].mobile)
    };
  }
  return {
    steps: ['Welcome', 'Personal Information', 'Employment & Income', 'Social Security & Pensions', 'Savings & Investments', 'Goals & Priorities', 'Assessment Results'],
    mobileSteps: ['Start', 'Info', 'Income', 'Benefits', 'Savings', 'Goals', 'Review']
  };
};



const getStepContent = (step: number, isSubmitted: boolean, setIsSubmitted: (value: boolean) => void, intakeVersion?: string, onNext?: () => void, hasPartner?: boolean, healthSpanResult?: any) => {
  // If form has been submitted, show thank you message instead
  if (isSubmitted) {
    return <ThankYouStep setIsSubmitted={setIsSubmitted} healthSpanResult={healthSpanResult} />;
  }

  // Basic (Limited) intake: step list is dynamic (HealthSpan variant adds health step(s))
  if (isBasicIntake(intakeVersion)) {
    const key = getBasicStepKeys(intakeVersion, hasPartner)[step];
    switch (key) {
      case 'start':
        return <WelcomeStep onNext={onNext} />;
      case 'about':
        return <BasicInfoStep />;
      case 'income':
        return <BasicFinancialsStep />;
      case 'optional':
        return <BasicOptionalStep />;
      case 'health':
        return <HealthSupportStep subject="primary" />;
      case 'healthPartner':
        return <HealthSupportStep subject="partner" />;
      case 'review':
        return <BasicReviewStep />;
      default:
        return 'Unknown step';
    }
  }

  // Full and demo intakes: 7-step flow (demo varies content within steps, not structure)
  switch (step) {
    case 0:
      return <WelcomeStep />;
    case 1:
      return <PersonalInfoStep />;
    case 2:
      return <EmploymentStep />;
    case 3:
      return <SocialSecurityStep />;
    case 4:
      return <SavingsStep />;
    case 5:
      return <GoalsStep />;
    case 6:
      return <ReviewStep />;
    default:
      return 'Unknown step';
  }
};

const Wizard: React.FC = () => {
  const { currentStep, setCurrentStep, wizardData, updateWizardData, clearWizardData } = useWizard();
  const { idCustomer } = useParams<{ idCustomer: string }>();
  const advisor = useAdvisor();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [healthSpanResult, setHealthSpanResult] = useState<any>(null);

  // Compute effective intake version: query param ?mode=basic overrides DB config
  const urlModeOverride = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode');
  }, []);

  const effectiveIntake = useMemo(() => {
    if (urlModeOverride === 'basic' || urlModeOverride === 'basic-healthspan') return urlModeOverride;
    return advisor?.intakeVersion || 'full';
  }, [urlModeOverride, advisor?.intakeVersion]);

  const hasPartner = wizardData.personal.hasPartner;
  const { steps, mobileSteps } = getStepsForIntake(effectiveIntake, hasPartner);
  
  // Initialize progress tracking for submission
  const { trackFormSubmission } = useProgressTracking({
    stepName: 'Form Submission',
    stepNumber: 7  // Using step 7 to indicate final submission
  });
  
  // Use advisor logo if available, otherwise use PlanScout logo
  const logoSrc = advisor?.companyLogoUrl || logo;
  const logoAlt = advisor ? `${advisor.company} Logo` : 'PlanScout Logo';

  // Initialize analytics and check for existing session on component mount
  useEffect(() => {
    // Initialize Application Insights
    analyticsService.initialize();

    // Check if there's an existing session to resume
    const urlParams = new URLSearchParams(window.location.search);
    const resumeSessionId = urlParams.get('resume') || urlParams.get('session'); // Support both 'resume' and 'session' parameters
    const prefillSessionId = urlParams.get('prefill'); // Upgrade flow: seed new session from a previous basic submission

    const loadSession = async () => {
      if (resumeSessionId) {
        // Try to load from server
        const savedProgress = await progressStorageService.loadProgress(resumeSessionId);
        if (savedProgress && savedProgress.formData) {
          // Resume from saved progress
          updateWizardData(savedProgress.formData);
          setCurrentStep(savedProgress.currentStep || 0);

          // Store the session ID in sessionStorage for tracking
          sessionStorage.setItem('wizardSessionId', resumeSessionId);

          console.log(`Resuming session ${resumeSessionId} at step ${savedProgress.currentStep}`);
        } else {
          console.warn(`Unable to load session ${resumeSessionId} - starting new session`);
          // Generate new session ID if load failed
          const newSessionId = progressStorageService.generateSessionId();
          sessionStorage.setItem('wizardSessionId', newSessionId);
          updateWizardData({
            sessionId: newSessionId,
            sessionStartTime: new Date().toISOString()
          });
        }
      } else if (prefillSessionId) {
        // Prefill mode: load data from a previous session but start a NEW session
        const savedProgress = await progressStorageService.loadProgress(prefillSessionId);
        if (savedProgress?.formData) {
          const newSessionId = progressStorageService.generateSessionId();
          sessionStorage.setItem('wizardSessionId', newSessionId);

          // Convert totalAssets to a savings entry if no breakdown was provided
          let prefillSavings = savedProgress.formData.savings;
          if (savedProgress.formData.totalAssets &&
              !savedProgress.formData.savings?.taxDeferred?.length &&
              !savedProgress.formData.savings?.taxFree?.length &&
              !savedProgress.formData.savings?.taxable?.length) {
            prefillSavings = {
              taxDeferred: [{ accountName: 'Retirement Savings', balance: savedProgress.formData.totalAssets, owner: 'client1' as const }],
              taxFree: [],
              taxable: []
            };
          }

          updateWizardData({
            ...savedProgress.formData,
            savings: prefillSavings,
            sessionId: newSessionId,
            sessionStartTime: new Date().toISOString(),
            wizardMode: 'full',
            prefillSessionId: prefillSessionId,
            totalAssets: undefined,
            completed: false,
            sipsCaseId: undefined,
            lastSaved: undefined,
          });

          setCurrentStep(0); // Start at Welcome so user can review
          console.log(`Prefilling from session ${prefillSessionId}, new session ${newSessionId}`);
        } else {
          console.warn(`Unable to load prefill session ${prefillSessionId} - starting fresh`);
          const newSessionId = progressStorageService.generateSessionId();
          sessionStorage.setItem('wizardSessionId', newSessionId);
          updateWizardData({
            sessionId: newSessionId,
            sessionStartTime: new Date().toISOString()
          });
        }
      } else {
        // Check if we have a session in sessionStorage
        const existingSessionId = sessionStorage.getItem('wizardSessionId');
        if (!existingSessionId) {
          // Generate new session ID if not present
          const newSessionId = progressStorageService.generateSessionId();
          sessionStorage.setItem('wizardSessionId', newSessionId);
          updateWizardData({
            sessionId: newSessionId,
            sessionStartTime: new Date().toISOString()
          });
          console.log(`Generated new session ID: ${newSessionId}`);
        }
      }
    };

    loadSession();
  }, []);

  // Log customer ID when it exists and update in wizard data.
  // Prefer the loaded advisor config's canonical portal customerId — the URL may
  // carry either the portal id ("adv-52253dbe") or the numeric SIPS id, and ids
  // are no longer necessarily numeric, so never parseInt them.
  useEffect(() => {
    if (idCustomer && !isSubmitted) {
      console.log('Customer ID:', idCustomer);
      const customerId = advisor?.customerId || idCustomer || DEFAULT_CUSTOMER_ID;
      updateWizardData({ customerId });

      // Track form started if at welcome step
      if (currentStep === 0 && wizardData.sessionId) {
        analyticsService.trackFormStarted(
          customerId.toString(),
          wizardData.sessionId
        );
      }
    }
  }, [idCustomer, advisor, isSubmitted, currentStep, wizardData.sessionId, updateWizardData]);

  // Set wizardMode based on effectiveIntake (once advisor config is loaded)
  useEffect(() => {
    if (advisor && !wizardData.wizardMode) {
      const wizardModeValue = isBasicIntake(effectiveIntake) ? 'basic' as const : 'full' as const;
      updateWizardData({ wizardMode: wizardModeValue });
    }
  }, [advisor, effectiveIntake, wizardData.wizardMode, updateWizardData]);

  // Listen for window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Scroll back to the top on every step change. Notably the primary → partner
  // health pages reuse the same <HealthSupportStep> at the same position, so React
  // re-renders it in place (no remount) and the scroll would otherwise stay put.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Check if the contact information is valid
  const isContactInfoValid = () => {
    const { personal } = wizardData;
    if (!personal?.contact?.email || !personal?.contact?.phone) return false;

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(personal.contact.email)) return false;

    // Phone validation
    const phoneRegex = /^[\d\+\-\(\) ]{10,15}$/;
    if (!phoneRegex.test(personal.contact.phone)) return false;

    return true;
  };

  // Helper function to check if a specific step is valid
  const isStepValid = (stepIndex: number) => {
    const { personal, goals, savings, employment, socialSecurity } = wizardData;

    // Basic (Limited) intake — validate by step key (HealthSpan variant adds a health step)
    if (isBasicIntake(effectiveIntake)) {
      const key = getBasicStepKeys(effectiveIntake, personal.hasPartner)[stepIndex];
      if (key === 'about') {
        // BasicInfoStep: name, age, partner
        if (!personal.client1.firstName || !personal.client1.lastName || !personal.client1.dateOfBirth) return false;
        const currentYear = new Date().getFullYear();
        const birthYear = parseInt(personal.client1.dateOfBirth.substring(0, 4), 10);
        const age = currentYear - birthYear;
        if (isNaN(age) || age < 18 || age > 100) return false;
        if (personal.hasPartner) {
          if (!personal.client2.firstName || !personal.client2.lastName || !personal.client2.dateOfBirth) return false;
          const partnerBirthYear = parseInt(personal.client2.dateOfBirth.substring(0, 4), 10);
          const partnerAge = currentYear - partnerBirthYear;
          if (isNaN(partnerAge) || partnerAge < 18 || partnerAge > 100) return false;
        }
        return true;
      }
      if (key === 'income') {
        // BasicFinancialsStep: total assets required
        return (wizardData.totalAssets || 0) > 0;
      }
      if (key === 'review') return isContactInfoValid(); // BasicReviewStep
      // start, optional, health — no blocking validation (health is optional)
      return true;
    }

    // Step 0: Welcome - always valid
    if (stepIndex === 0) {
      return true;
    }

    // Step 1: Personal Info
    if (stepIndex === 1) {
      // Check if first name, last name, and DOB are provided
      if (!personal.client1.firstName || !personal.client1.lastName || !personal.client1.dateOfBirth) {
        return false;
      }
      
      // Validate DOB is not in the future and person is at least 18
      const dobDate = new Date(personal.client1.dateOfBirth);
      const currentDate = new Date();
      const eighteenYearsAgo = new Date();
      eighteenYearsAgo.setFullYear(currentDate.getFullYear() - 18);
      
      if (dobDate > currentDate || dobDate > eighteenYearsAgo) {
        return false;
      }
      
      // If they have a partner, check partner info
      if (personal.hasPartner) {
        if (!personal.client2.firstName || !personal.client2.lastName || !personal.client2.dateOfBirth) {
          return false;
        }
        
        // Validate partner DOB
        const partnerDobDate = new Date(personal.client2.dateOfBirth);
        if (partnerDobDate > currentDate || partnerDobDate > eighteenYearsAgo) {
          return false;
        }
      }
      return true;
    }
    
    // Step 2: Employment
    if (stepIndex === 2) {
      // Calculate current ages
      const calculateAge = (dateOfBirth: string) => {
        if (!dateOfBirth) return 0;
        const today = new Date();
        const birthDate = new Date(dateOfBirth);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        return age;
      };
      
      const currentAge = calculateAge(personal.client1.dateOfBirth);
      const partnerCurrentAge = calculateAge(personal.client2.dateOfBirth);
      
      // Check if retirement age is valid for non-retired client
      if (!employment.client1.isRetired && employment.client1.currentIncome > 0 && employment.client1.expectedRetirementAge) {
        if (currentAge > 0 && employment.client1.expectedRetirementAge < currentAge) {
          return false;
        }
      }
      
      // Check if partner's retirement age is valid for non-retired partner
      if (personal.hasPartner && !employment.client2.isRetired && 
          employment.client2.currentIncome > 0 && employment.client2.expectedRetirementAge) {
        if (partnerCurrentAge > 0 && employment.client2.expectedRetirementAge < partnerCurrentAge) {
          return false;
        }
      }
      return true;
    }
    
    // Step 3: Social Security - Check if basic fields are filled
    if (stepIndex === 3) {
      // Check if at least one SS field is filled for client1
      const hasClient1SS = socialSecurity.client1.currentAmount ||
                           socialSecurity.client1.projectedAmount ||
                           socialSecurity.client1.claimingAge;

      if (!hasClient1SS) {
        return false;
      }

      // If has partner, check partner's SS info
      if (personal.hasPartner) {
        const hasClient2SS = socialSecurity.client2.currentAmount ||
                             socialSecurity.client2.projectedAmount ||
                             socialSecurity.client2.claimingAge;

        if (!hasClient2SS) {
          return false;
        }
      }
      return true;
    }
    
    // Step 4: Savings
    if (stepIndex === 4) {
      // Calculate the grand total from all accounts
      const grandTotal =
        savings.taxDeferred.reduce((sum, acc) => sum + acc.balance, 0) +
        savings.taxFree.reduce((sum, acc) => sum + acc.balance, 0) +
        savings.taxable.reduce((sum, acc) => sum + acc.balance, 0);

      // Check if grand total is greater than zero
      if (grandTotal <= 0) {
        return false;
      }
      return true;
    }
    
    // Step 5: Goals
    if (stepIndex === 5) {
      // Check if target income is provided and greater than zero
      if (!goals.targetIncome || goals.targetIncome <= 0) {
        return false;
      }
      return true;
    }
    
    // Step 6: Review - always valid if reached
    if (stepIndex === 6) {
      return true;
    }
    
    return true;
  };

  // Check if the current step is valid
  const isCurrentStepValid = () => {
    return isStepValid(currentStep);
  };
  
  // Check if all required steps are complete for submission
  const areAllRequiredStepsComplete = () => {
    if (isBasicIntake(effectiveIntake)) {
      const keys = getBasicStepKeys(effectiveIntake, wizardData.personal.hasPartner);
      return isStepValid(keys.indexOf('about')) && isStepValid(keys.indexOf('income'));
    }
    // Check steps 1-5 (skip Welcome and Review)
    for (let i = 1; i <= 5; i++) {
      if (!isStepValid(i)) {
        return false;
      }
    }
    return true;
  };

  useEffect(() => {
    // Prefer the advisor config's canonical portal customerId (the URL may carry
    // the numeric SIPS id instead); fall back to the raw URL id, then the default.
    // Ids may be non-numeric ("adv-52253dbe") — never parseInt them.
    const customerId = advisor?.customerId || idCustomer || DEFAULT_CUSTOMER_ID;
    if (String(customerId) !== String(wizardData.customerId ?? '')) {
      // Only update if the customerId has changed
      updateWizardData({
        customerId
      });
    }
  }, [idCustomer, advisor, updateWizardData, wizardData.customerId]);

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      // Set submitting state to true
      setIsSubmitting(true);

      // Submit to TaxAlpha API with advisor-specific configuration
      const response = await submitToTaxAlpha(wizardData, advisor);

      // Extract SIPS case ID from response
      const sipsCaseId = response?.caseId;
      console.log('TaxAlpha Case ID received:', sipsCaseId);

      // Capture the qualitative HealthSpan themes (basic-healthspan only) for the end screen
      setHealthSpanResult(response?.healthSpanResult || null);

      // Set success state
      setSubmitError(null);

      // Mark as completed in wizard data and store TaxAlpha case ID
      const updatedData = {
        ...wizardData,
        completed: true,
        sipsCaseId: sipsCaseId
      };
      updateWizardData({
        completed: true,
        sipsCaseId: sipsCaseId
      });

      // Track form submission and mark progress as 100% complete with updated data
      // Ensure sipsCaseId is saved to database by passing it explicitly
      if (wizardData.sessionId) {
        await progressStorageService.saveProgress(
          updatedData,
          7, // Final step
          'Form Submission',
          wizardData.sessionId,
          true // Mark as complete
        );
      }

      // Track analytics
      if (wizardData.sessionId && wizardData.sessionStartTime) {
        const totalTime = Date.now() - new Date(wizardData.sessionStartTime).getTime();
        analyticsService.trackFormSubmitted(
          wizardData.sessionId,
          wizardData.customerId?.toString() || 'unknown',
          totalTime
        );
      }

      // Clear session data after successful submission
      // Data remains on server but clear local session reference
      setTimeout(() => {
        sessionStorage.removeItem('wizardSessionId');
        updateWizardData({
          sessionId: undefined,
          sessionStartTime: undefined,
          lastSaved: undefined
          // Keep completed: true and sipsCaseId to prevent saves on Thank You page
        });
      }, 100);

      // Show the thank you screen
      setIsSubmitted(true);

    } catch (error) {
      // Handle error
      console.error('Error submitting data:', error);
      setSubmitError('Failed to submit your data. Please try again later.');
    } finally {
      // Reset submitting state
      setIsSubmitting(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSubmitError(null);
  };

  return (
    <Box sx={{
      width: '100%',
      maxWidth: 1000,
      mx: 'auto',
      p: { xs: 2, sm: 3 }
    }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 4 },
          borderRadius: 2
        }}
      >
        {/* Logo - moved above breadcrumbs */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: isSubmitted ? 1.5 : 4,
            '& img': {
              height: { xs: 90, sm: 120 },
              width: 'auto',
              maxWidth: '500px'
            }
          }}
        >
          <Box
            component="img"
            src={logoSrc}
            alt={logoAlt}
          />
        </Box>

        {/* Mobile step indicator */}
        {!isSubmitted && isMobile && (
          <Box sx={{ 
            display: { xs: 'flex', sm: 'none' }, 
            justifyContent: 'center', 
            mb: 2, 
            alignItems: 'center'
          }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Step {currentStep + 1} of {steps.length}: {steps[currentStep]}
            </Typography>
          </Box>
        )}

        {!isSubmitted && (
          <Stepper 
            activeStep={currentStep} 
            sx={{ 
              mb: 4,
              '& .MuiStepLabel-label': {
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                lineHeight: 1.2,
                textAlign: 'center',
                cursor: 'pointer',
                display: { xs: 'none', sm: 'block' } // Hide text labels on mobile
              },
              '& .MuiStepper-root': {
                flexWrap: 'wrap',
              },
              '& .MuiStep-root': {
                py: { xs: 0.5, sm: 1 }, // Reduce padding on mobile
                px: { xs: 0.5, sm: 1 },
              },
              '& .MuiStepConnector-root': {
                mx: { xs: -0.5, sm: 0 } // Tighten connectors on mobile
              },
              '& .MuiStepIcon-root.Mui-completed': {
                color: '#2BA784', // Green for completed steps
              },
              '& .MuiStepIcon-root.Mui-active': {
                color: '#2BA784', // Green for active step
              }
            }}
            alternativeLabel
          >
            {(isMobile ? mobileSteps : steps).map((label, index) => {
              const isStepCompleted = index < currentStep || (index <= currentStep && isStepValid(index));
              const isStepIncomplete = index < currentStep && !isStepValid(index);
              
              return (
                <Step key={label} completed={isStepCompleted && !isStepIncomplete}>
                  <StepLabel 
                    onClick={() => {
                      // Only allow clicking on completed steps or the next available step
                      if (index <= currentStep + 1) {
                        setCurrentStep(index);
                      }
                    }}
                    error={isStepIncomplete}
                    // Show tooltips on mobile to compensate for hidden labels
                    componentsProps={{
                      label: {
                        title: isMobile ? steps[index] : label, // Show full name in tooltip
                      }
                    }}
                    sx={{
                      '& .MuiStepIcon-root': {
                        color: isStepIncomplete ? '#ff9800' : undefined, // Orange for incomplete
                      },
                      '& .MuiStepIcon-root.Mui-completed': {
                        color: isStepIncomplete ? '#ff9800' : undefined, // Orange for incomplete even if visited
                      },
                      '& .MuiStepIcon-text': {
                        fill: isStepIncomplete ? '#fff' : undefined,
                      }
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        )}

        <Box sx={{
          minHeight: 400,
          maxWidth: 900,
          mx: 'auto'
        }}>
          {getStepContent(currentStep, isSubmitted, setIsSubmitted, effectiveIntake, handleNext, hasPartner, healthSpanResult)}
        </Box>

        {!isSubmitted && !(currentStep === 0 && isBasicIntake(effectiveIntake)) && (
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 3,
            maxWidth: 900,
            mx: 'auto'
          }}>
            <Button
              disabled={currentStep === 0}
              onClick={handleBack}
              sx={{ color: '#2BA784', '&:hover': { bgcolor: 'rgba(43, 167, 132, 0.08)' } }}
            >
              Back
            </Button>

            {currentStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isContactInfoValid() || !areAllRequiredStepsComplete() || isSubmitting}
                startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{ bgcolor: '#2BA784', '&:hover': { bgcolor: '#249573' } }}
              >
                {isSubmitting ? 'Submitting...' : isBasicIntake(effectiveIntake) ? 'Submit to Advisor' : 'Get my detailed plan'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isCurrentStepValid()}
                sx={{ bgcolor: '#2BA784', '&:hover': { bgcolor: '#249573' } }}
              >
                Next
              </Button>
            )}
          </Box>
        )}
      </Paper>

      <Snackbar
        open={!!submitError}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message={submitError}
        action={
          <IconButton
            size="small"
            color="inherit"
            onClick={handleSnackbarClose}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </Box>
  );
};

export default Wizard; 